import React, { useCallback } from 'react';

import { VendorPicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/VendorPicker';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: any) => any) => void} props.onDraftChange
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.primaryErrors]
 * @param {Object} [props.primaryFieldErrors]
 */
export function PackagingPOCreatePrimaryCard({
    draft,
    onDraftChange,
    primaryErrors = [],
    primaryFieldErrors = {},
    showValidation = false,
}) {
    const setDraftField = useCallback(
        (field, value) => {
            onDraftChange((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [onDraftChange],
    );

    const handleInputChange = useCallback(
        (field) => (event) => {
            const value = event?.target?.value ?? '';
            setDraftField(field, value);
            if (field === 'scheduledDeliveryDate' && value && !draft.scheduledTime) {
                setDraftField('scheduledTime', '00:00');
            }
        },
        [draft.scheduledTime, setDraftField],
    );

    const showErrors = showValidation === true;
    const getFieldError = useCallback(
        (field) => {
            if (!showErrors) return '';
            return primaryFieldErrors?.[field] ?? '';
        },
        [primaryFieldErrors, showErrors],
    );
    const renderFieldError = useCallback(
        (field) => {
            const message = getFieldError(field);
            if (!message) return null;
            return <p className="text-xs text-destructive">{message}</p>;
        },
        [getFieldError],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Purchase Order Information</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showErrors && primaryErrors.length > 0 ? (
                    <div className="pb-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following to save this purchase order</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {primaryErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-12">
                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <VendorPicker
                            value={draft.vendorId}
                            onValueChange={(id) => setDraftField('vendorId', id)}
                            label={
                                <>
                                    Vendor <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {renderFieldError('vendorId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <FacilityPickerV2
                            label={
                                <>
                                    Facility <span className="text-destructive">*</span>
                                </>
                            }
                            value={draft.facilities}
                            onValueChange={(facilities) => setDraftField('facilities', facilities)}
                            multiSelect
                        />
                        {renderFieldError('facilityId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                            Scheduled Delivery Date <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="scheduledDeliveryDate"
                            type="date"
                            value={draft.scheduledDeliveryDate}
                            onChange={handleInputChange('scheduledDeliveryDate')}
                        />
                        {renderFieldError('scheduledDeliveryDate')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="scheduledDeliveryTime" className={labelClassName}>
                            Scheduled Delivery Time
                        </Label>
                        <Input
                            id="scheduledDeliveryTime"
                            type="time"
                            step="60"
                            value={draft.scheduledTime}
                            onChange={handleInputChange('scheduledTime')}
                        />
                        {renderFieldError('scheduledTime')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="referenceNumber" className={labelClassName}>
                            Reference Number
                        </Label>
                        <Input
                            id="referenceNumber"
                            type="text"
                            value={draft.referenceNumber}
                            onChange={handleInputChange('referenceNumber')}
                        />
                        {renderFieldError('referenceNumber')}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

PackagingPOCreatePrimaryCard.displayName = 'PackagingPOCreatePrimaryCard';
